import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/db8gn0okk00l6eltrk30";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");
      const okCode = parsed?.code === "OK";

      if (response.ok && okCode && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  const inputClass =
    "w-full h-11 px-3.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-950 placeholder:text-foreground-400 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-100";

  return (
    <form
      id="contact-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6"
    >
      <h3 className="font-heading font-bold text-lg text-foreground-950 mb-5">
        在线留言与反馈
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            您的称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="请输入您的称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            联系邮箱
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="your@email.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
          反馈主题
        </label>
        <select
          id="contact-subject"
          name="subject"
          className={`${inputClass} cursor-pointer`}
          defaultValue="内容建议"
        >
          <option value="内容建议">内容建议</option>
          <option value="资源纠错">资源纠错</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
          留言内容
          <span className="text-foreground-400 ml-1">（最多 500 字）</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请简要描述您的建议或问题…"
          className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-950 placeholder:text-foreground-400 outline-none transition resize-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {/* 防垃圾信息字段 */}
      <input
        className="info-supplement-field"
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        className="mt-5 w-full sm:w-auto px-7 h-11 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line"></i>
        提交反馈
      </button>

      {status === "success" && (
        <p className="mt-4 text-sm text-secondary-700 bg-secondary-100 rounded-md px-3.5 py-2.5" role="status">
          感谢您的反馈，我们已经收到您的留言，会尽快处理。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 text-sm text-primary-700 bg-primary-50 rounded-md px-3.5 py-2.5" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
}